import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import Icon from './Icon'

type PagerProps = {
  page: number
  totalPages: number
  onChange: (page: number) => void
}

const ELLIPSIS = 'ellipsis'

/** Build a windowed page list: first, last, current ±1, with ellipses between gaps. */
const buildPageList = (page: number, totalPages: number): (number | typeof ELLIPSIS)[] => {
  if (totalPages <= 7) return Array.from({length: totalPages}, (_, i) => i + 1)
  const pages = new Set<number>([1, totalPages, page, page - 1, page + 1])
  const sorted = [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b)
  const out: (number | typeof ELLIPSIS)[] = []
  let prev = 0
  for (const p of sorted) {
    if (p - prev > 1) out.push(ELLIPSIS)
    out.push(p)
    prev = p
  }
  return out
}

const Pager = ({page, totalPages, onChange}: PagerProps) => {
  const {t} = useTranslation()
  const items = useMemo(() => buildPageList(page, totalPages), [page, totalPages])
  if (totalPages <= 1) return null

  return (
    <nav className="tpl-pager" aria-label={t('a11y.pagination')}>
      <button
        type="button"
        className="tpl-pager-btn tpl-pager-arrow"
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
        aria-label={t('a11y.previousPage')}
      >
        <Icon name="chevron" size={14} />
      </button>
      {items.map((it, idx) =>
        it === ELLIPSIS ? (
          <span key={`e${idx}`} className="tpl-pager-ellipsis">
            …
          </span>
        ) : (
          <button
            key={it}
            type="button"
            className={`tpl-pager-btn ${it === page ? 'active' : ''}`}
            onClick={() => onChange(it)}
            aria-current={it === page ? 'page' : undefined}
          >
            {it}
          </button>
        ),
      )}
      <button
        type="button"
        className="tpl-pager-btn tpl-pager-arrow tpl-pager-next"
        onClick={() => onChange(page + 1)}
        disabled={page >= totalPages}
        aria-label={t('a11y.nextPage')}
      >
        <Icon name="chevron" size={14} />
      </button>
    </nav>
  )
}

export default Pager
